// Local store for candidacy edit tokens. Verifying ownership (the company-name
// proof) returns a per-USER token that edits every campaign that user owns, so we
// keep one global token alongside any legacy per-candidacy tokens. Losing it means
// re-verifying (by design — there's no account system).

import { trackEvent } from "./analytics";

const key = (candidacyId: string) => `candidacy:${candidacyId}:token`;
const USER_KEY = "warurna:userToken";

const read = (k: string): string | null => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};

export const editToken = {
  /** The token to send for a candidacy: its own legacy per-candidacy token if
   * present, else the global per-user (claimed) token.
   *
   * Lazily migrates a legacy token to the global key on first read (when no global
   * token exists yet), so a returning verified user gets the per-user "edit all"
   * behaviour for free and we converge on a single key. Harmless if the legacy
   * token is stale — the server rejects it the same as an empty slot. */
  get: (candidacyId: string): string | null => {
    const legacy = read(key(candidacyId));
    if (legacy && !read(USER_KEY)) {
      editToken.setUser(legacy);
      trackEvent("edit-token-migrated"); // legacy per-campaign token promoted to the per-user key
    }
    return legacy ?? read(USER_KEY);
  },
  /** The per-user (claimed) token if this device holds one, else null. Used to
   * fold into poll-vote dedup (1 token = 1 vote) — never for edit auth. */
  getUser: (): string | null => read(USER_KEY),
  /** Store a per-user (claimed) token — unlocks every campaign the user owns. */
  setUser: (token: string): void => {
    try {
      localStorage.setItem(USER_KEY, token);
    } catch {
      /* private mode / storage full — management just won't persist */
    }
  },
  clear: (candidacyId: string): void => {
    try {
      localStorage.removeItem(key(candidacyId));
      localStorage.removeItem(USER_KEY); // also drop the per-user token (the credential a verified owner holds)
    } catch {
      /* ignore */
    }
  },
  /** Drop only the per-user token (the global credential) — "forget" from the
   * account menu, without touching any legacy per-candidacy key. */
  clearUser: (): void => {
    try {
      localStorage.removeItem(USER_KEY);
    } catch {
      /* ignore */
    }
  },
};
